import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { Vehicles } from './pages/transport/Vehicles';
import { Licences } from './pages/transport/Licences';
import { Roadworthiness } from './pages/transport/Roadworthiness';
import { Psv } from './pages/transport/Psv';
import { Fines } from './pages/transport/Fines';
import { Tests } from './pages/transport/Tests';
import { Vessels } from './pages/maritime/Vessels';
import { AgeExemptions } from './pages/maritime/AgeExemptions';
import { Seafarers } from './pages/maritime/Seafarers';
import { PortCalls } from './pages/maritime/PortCalls';
import { CharterPermits } from './pages/maritime/CharterPermits';
import { CruisePermits } from './pages/maritime/CruisePermits';
import { SmallCraft } from './pages/maritime/SmallCraft';
import { UasRegistrations } from './pages/aviation/UasRegistrations';
import { UasOperations } from './pages/aviation/UasOperations';
import { AirspaceZones } from './pages/aviation/AirspaceZones';
import { HeightClearances } from './pages/aviation/HeightClearances';
import { AerodromePermits } from './pages/aviation/AerodromePermits';
import { AocCases } from './pages/aviation/AocCases';
import { Shell, type NavHeader, type NavItem } from './ui/Shell';

const NAV: Array<NavItem | NavHeader> = [
  { header: 'Land Transport — M21 (LTA)' },
  { to: '/transport/vehicles', label: 'Vehicle Registry', icon: '🚗', exact: false },
  { to: '/transport/licences', label: 'Driver Licences', icon: '🪪', exact: false },
  { to: '/transport/roadworthiness', label: 'Roadworthiness', icon: '🔧', exact: false },
  { to: '/transport/psv', label: 'PSV Permits', icon: '🚕', exact: false },
  { to: '/transport/fines', label: 'Traffic Fines', icon: '🎫', exact: false },
  { to: '/transport/tests', label: 'Driving Tests', icon: '📝' },

  { header: 'Maritime & Ports — M22 (SMSA / SPA)' },
  { to: '/maritime/vessels', label: 'Vessel Registry', icon: '⚓', exact: false },
  { to: '/maritime/age-exemptions', label: 'Age Exemptions', icon: '📜', exact: false },
  { to: '/maritime/seafarers', label: 'Seafarers', icon: '🧑‍✈️', exact: false },
  { to: '/maritime/port-calls', label: 'Port Calls', icon: '🛳', exact: false },
  { to: '/maritime/charter-permits', label: 'Charter Permits', icon: '⛵', exact: false },
  { to: '/maritime/cruise-permits', label: 'Cruise Permits', icon: '🚢', exact: false },
  { to: '/maritime/small-craft', label: 'Small Craft Licences', icon: '🛶', exact: false },

  { header: 'Civil Aviation — M23 (SCAA)' },
  { to: '/aviation/uas-registrations', label: 'UAS / Drone Registrations', icon: '🛸', exact: false },
  { to: '/aviation/uas-operations', label: 'UAS Operations', icon: '🗺', exact: false },
  { to: '/aviation/airspace-zones', label: 'Airspace Zones', icon: '🚫', exact: false },
  { to: '/aviation/height-clearances', label: 'Height Clearances', icon: '📏', exact: false },
  { to: '/aviation/aerodrome-permits', label: 'Aerodrome Permits', icon: '🛫', exact: false },
  { to: '/aviation/aoc-cases', label: 'AOC Cases', icon: '📋', exact: false },
];

const ROLE_TO_LANDING: Record<string, string> = {
  LTA_OFFICER: '/transport/vehicles',
  EXAMINER: '/transport/tests',
  PSV_BOARD: '/transport/psv',
  STATION_INSPECTOR: '/transport/roadworthiness',
  FINES_ADMIN: '/transport/fines',
  SHIP_REGISTRAR: '/maritime/vessels',
  MARINE_SURVEYOR: '/maritime/vessels',
  PORT_OFFICER: '/maritime/port-calls',
  SEAFARER_REGISTRAR: '/maritime/seafarers',
  UAS_OFFICER: '/aviation/uas-registrations',
  AIRSPACE_OFFICER: '/aviation/airspace-zones',
  FLIGHT_OPS_INSPECTOR: '/aviation/aerodrome-permits',
};

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Land each persona somewhere useful for their primary duty rather than
  // always defaulting to the first workspace in the sidebar.
  const landing = persona.roles.map((r) => ROLE_TO_LANDING[r]).find(Boolean) ?? '/transport/vehicles';

  return (
    <Routes>
      <Route element={<Shell title={persona.org} items={NAV} />}>
        <Route index element={<Navigate to={landing} replace />} />

        <Route path="transport/vehicles" element={<Vehicles />} />
        <Route path="transport/licences" element={<Licences />} />
        <Route path="transport/roadworthiness" element={<Roadworthiness />} />
        <Route path="transport/psv" element={<Psv />} />
        <Route path="transport/fines" element={<Fines />} />
        <Route path="transport/tests" element={<Tests />} />

        <Route path="maritime/vessels" element={<Vessels />} />
        <Route path="maritime/age-exemptions" element={<AgeExemptions />} />
        <Route path="maritime/seafarers" element={<Seafarers />} />
        <Route path="maritime/port-calls" element={<PortCalls />} />
        <Route path="maritime/charter-permits" element={<CharterPermits />} />
        <Route path="maritime/cruise-permits" element={<CruisePermits />} />
        <Route path="maritime/small-craft" element={<SmallCraft />} />

        <Route path="aviation/uas-registrations" element={<UasRegistrations />} />
        <Route path="aviation/uas-operations" element={<UasOperations />} />
        <Route path="aviation/airspace-zones" element={<AirspaceZones />} />
        <Route path="aviation/height-clearances" element={<HeightClearances />} />
        <Route path="aviation/aerodrome-permits" element={<AerodromePermits />} />
        <Route path="aviation/aoc-cases" element={<AocCases />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
